import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, MicOff, Square } from "lucide-react";

/**
 * VoiceRecorder — records a short voice clip via MediaRecorder and returns the
 * resulting Blob via `onComplete(blob)`. Auto-stops at `maxSeconds`.
 *
 * Premium-only feature; the parent (MockInterview) decides whether to render it.
 */
export default function VoiceRecorder({ maxSeconds = 90, onComplete, onError, disabled = false }) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const intervalRef = useRef(null);

  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* already stopped */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRecording(false);
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined);
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size > 0 && onComplete) onComplete(blob);
      };
      rec.start();
      setIsRecording(true);
      setElapsed(0);

      const startedAt = Date.now();
      intervalRef.current = setInterval(() => {
        const e = Math.floor((Date.now() - startedAt) / 1000);
        setElapsed(e);
        if (e >= maxSeconds) stop();
      }, 250);
    } catch (err) {
      onError && onError(err.message || "Microphone access denied.");
    }
  };

  const remaining = useMemo(() => Math.max(0, maxSeconds - elapsed), [maxSeconds, elapsed]);

  return (
    <div className="flex flex-col items-start gap-3" data-testid="voice-recorder">
      {!isRecording ? (
        <button
          type="button"
          onClick={start}
          disabled={disabled}
          data-testid="voice-record-start"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors"
        >
          <Mic className="w-4 h-4" /> Record voice answer
        </button>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={stop}
            data-testid="voice-record-stop"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#DC1C22] text-white text-sm font-medium hover:bg-[#A8141A] transition-colors animate-pulse"
          >
            <Square className="w-4 h-4 fill-current" /> Stop ({remaining}s left)
          </button>
          <span className="text-xs font-mono text-zinc-500">{elapsed}s / {maxSeconds}s</span>
        </div>
      )}
      <p className="text-[11px] text-zinc-500">
        <MicOff className="inline w-3 h-3 mr-1 align-text-bottom" />
        Up to {maxSeconds}s · we transcribe with Whisper, then AI grades the response.
      </p>
    </div>
  );
}
